import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Form,
  Row,
  Table,
  message,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';

import SettingsCardHeader from '../SettingsCardHeader';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import CompanySettingsCard from '../CompanySettingsCard';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import Colors from '../../constants/Colors';
import CustomCostcodeCategoryAddEditDrawer from './CustomCostcodeCategoryAddEditDrawer';
import useToggle from '../../common/hooks/useToggle';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';

import {
  createCustomCostcodeCategory,
  updateCustomCostcodeCategory,
  deleteCustomCostcodeCategory,
} from '../../costcodes/state/costcodes.actions';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';

const onHeaderCell = () => ({
  style: {
    backgroundColor: 'white',
  },
});

export default function CostcodeCategories() {
  const dispatch = useDispatch();

  const [form] = Form.useForm();

  const company = useSelector((state) => state.settings.company);
  const categoriesMap = useSelector((state) => state.costcodes.categoriesIdMap);

  const [selectedEntry, setSelectedEntry] = useState();

  const {
    isToggled: showDrawer,
    toggle: toggleDrawer,
  } = useToggle();

  const hasRedesign = company?.settings?.uxRedesignPt1;

  const closeModal = useCallback(() => {
    setSelectedEntry();
    toggleDrawer();
  }, [toggleDrawer]);

  const onSave = useCallback(async () => {
    try {
      const payload = await form.validateFields();
      payload.allowTimeTracking = !!payload.allowTimeTracking;
      payload.allowInvoiceUpload = !!payload.allowInvoiceUpload;
      payload.allowQuantityTracking = !!payload.allowQuantityTracking;

      const hasAtLeastOneSetting = payload.allowTimeTracking
        || payload.allowInvoiceUpload
        || payload.allowQuantityTracking;

      if (!hasAtLeastOneSetting) {
        message.error('At least one setting must be enabled');
        return;
      }

      const result = selectedEntry
        ? await dispatch(updateCustomCostcodeCategory(selectedEntry.id, payload))
        : await dispatch(createCustomCostcodeCategory(payload));

      if (result) closeModal();
    } catch (err) {
      // fail silently - form validation will show errors
    }
  }, [closeModal, form, selectedEntry]);

  const onDelete = useCallback((record) => {
    const { id } = record || {};
    if (typeof id !== 'string') return; // default categories

    CustomConfirmModal({
      title: `Delete ${record.name}?`,
      content: 'Are you sure you want to delete this category?',
      onOk: async () => {
        const result = await dispatch(deleteCustomCostcodeCategory(id));
        if (result && selectedEntry) closeModal();
      },
      okText: 'Delete',
    });
  }, [closeModal, selectedEntry]);

  useEffect(() => {
    if (!showDrawer) {
      setSelectedEntry();
      form.resetFields();
      return;
    }

    if (selectedEntry) {
      form.setFieldsValue(selectedEntry);
      return;
    }

    // Default values for new category
    form.setFieldsValue({ allowInvoiceUpload: true });
  }, [showDrawer, form, selectedEntry]);

  const sortedCategories = useMemo(() => (
    Object.values(categoriesMap || {}).sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return a.name.localeCompare(b.name);
    })
  ), [categoriesMap]);

  const columns = useMemo(() => [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      onHeaderCell,
    },
    {
      title: 'Time Tracking',
      dataIndex: 'allowTimeTracking',
      key: 'allowTimeTracking',
      onHeaderCell,
      render: (allowTimeTracking) => (
        <OnTraccrCheckbox
          value={allowTimeTracking}
          disabled
          isRedesign
        />
      ),
    },
    {
      title: 'Invoice Upload',
      dataIndex: 'allowInvoiceUpload',
      key: 'allowInvoiceUpload',
      onHeaderCell,
      render: (allowInvoiceUpload) => (
        <OnTraccrCheckbox
          value={allowInvoiceUpload}
          disabled
          isRedesign
        />
      ),
    },
    {
      title: 'Quantity Tracking',
      dataIndex: 'allowQuantityTracking',
      key: 'allowQuantityTracking',
      onHeaderCell,
      render: (allowQuantityTracking) => (
        <OnTraccrCheckbox
          value={allowQuantityTracking}
          disabled
          isRedesign
        />
      ),
    },
    {
      dataIndex: 'edit',
      align: 'edit',
      width: 50,
      onHeaderCell,
      render: (_, record) => {
        const { id, isDefault } = record || {};
        if (isDefault) return null;
        return (
          <BorderlessButton
            key={id}
            title=""
            icon="edit"
            color={Colors.ONTRACCR_BLACK}
            onClick={() => {
              setSelectedEntry(record);
              toggleDrawer();
            }}
            style={{
              paddingRight: 8,
              paddingLeft: 0,
            }}
          />
        );
      },
    },
    {
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      width: 60,
      onHeaderCell,
      render: (_, record) => {
        const { id, isDefault } = record || {};
        if (isDefault) return null;
        return (
          <BorderlessButton
            key={id}
            title=""
            icon="delete"
            color={Colors.opacity(Colors.ONTRACCR_RED)}
            onClick={() => {
              onDelete(record);
            }}
            style={{
              paddingRight: 8,
              paddingLeft: 0,
              cursor: 'pointer',
            }}
          />
        );
      },
    },
  ], [onDelete, toggleDrawer]);

  return (
    <Row
      id="divisions-container"
      style={{
        width: '100%',
        height: '100%',
        marginLeft: 0,
        marginTop: 30,
      }}
    >
      <SettingsCardHeader title="Costcode Categories">
        <OnTraccrButton
          title="Add"
          type="primary"
          onClick={toggleDrawer}
        />
      </SettingsCardHeader>
      <CompanySettingsCard
        style={{
          position: 'absolute',
          top: hasRedesign ? 175 : 130,
          bottom: 20,
          padding: 0,
          height: 'auto',
        }}
      >
        <Table
          columns={columns}
          dataSource={sortedCategories}
          pagination={false}
          size="small"
        />
      </CompanySettingsCard>
      <CustomCostcodeCategoryAddEditDrawer
        selectedEntry={selectedEntry}
        visible={showDrawer}
        toggleDrawer={toggleDrawer}
        onSave={onSave}
        onDelete={onDelete}
        form={form}
      />
    </Row>
  );
}
